/**
=========================================================
* Trust Cargo - notification settings
=========================================================

* How long after its date each notice is raised, and whether it is raised at
* all. Every "two days after loading" in the system is one row here.
*
* Minutes exist for testing. A workflow that only shows itself over ten days
* cannot be checked by looking at it, so a step set to minutes lets the whole
* chain be watched firing in the time it takes to make a cup of tea. Counting
* in minutes runs from when the record was created rather than from its
* loading date - a date column has no time on it, so "two minutes after the
* loading date" would mean two minutes past midnight and the notice would
* appear at once instead of in two minutes.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

/**
 * When each notice actually fires, in full.
 *
 * The number on the row says how long after; this says after what, and what
 * has to be true for it to be raised at all - which is the half that was
 * only ever written in the controllers. Every one of them also disappears on
 * its own when the thing it asks for happens, which is said here too.
 */
const WHEN = {
  "container.cbm_prices":
    "لكل حاوية إلها تاريخ تحميل وفيها زبائن. بيظهر بعد المدة من تاريخ التحميل طالما في زبون واحد على الأقل سعره فاضي بصفحة الحاوية (View Container)، وبيقول كم واحد ناقص. ما إله زر «تم» - بيختفي لحاله أول ما تتعبّى كل الأسعار. الضغط عليه بيفتح صفحة الأسعار، و«تخطي» بتأجّله.",
  "container.invoices_posted":
    "لكل حاوية إلها تاريخ تحميل. بيظهر بعد المدة المحددة من تاريخ التحميل، وبيختفي لما تضغط «تم».",
  "container.bol_requested":
    "لكل حاوية إلها تاريخ تحميل. بيظهر بعد المدة من تاريخ التحميل طالما واحد من الثلاثة لسه مش مشغّل على الحاوية: طلب البوليصة، طلب كشف شركة الشحن، طلب كشف تكاليف التحميل - وبيقول أي واحد ناقص. بيختفي لحاله لما يتشغّلوا الثلاثة، أو لما تضغط «تم» وهي بتشغّل الباقي.",
  "container.clearance_file":
    "لكل حاوية إلها تاريخ تحميل. بيظهر بعد المدة من تاريخ التحميل، وبيختفي لما تضغط «تم».",
  "container.port_inspection":
    "بس إذا الحاوية إلها تاريخ وصول متوقع. بيظهر بعد المدة من ذاك التاريخ. «تم» بتطلب منك تاريخ الفحص وبتسجّله على الحاوية - وهو اللي بيشغّل إشعار «وصول الحاوية إلى الخليل».",
  "container.hebron_arrival":
    "بس بعد ما ينتسجّل تاريخ فحص الميناء. بيظهر بعد المدة من تاريخ الفحص. «تم» بتطلب تاريخ الاستلام وبتسجّله - وهو اللي بيشغّل إشعاري أجور الشحن والبيان الجمركي.",
  "container.shipping_fees":
    "بس بعد ما ينتسجّل تاريخ استلام الحاوية. بيظهر بعد المدة من تاريخ الاستلام، وبيختفي لما تضغط «تم».",
  "container.customs_declaration":
    "بس بعد ما ينتسجّل تاريخ استلام الحاوية. بيظهر بعد المدة من تاريخ الاستلام، وبيختفي لما تضغط «تم».",
  "container.snooze":
    "مش إشعار - هاي مدة رجوع أي إشعار حاوية ضغطت عليه «تخطي». بيرجع يظهر بعدها لحاله.",

  "order.check":
    "بس للطلبيات نوع We Pay وغير الملغية. بيظهر بعد المدة من تاريخ الطلبية، وبس إذا ما انسجّل عربون للمورد على هالطلبية. بيختفي لحاله أول ما تسجّل العربون - ما إله زر «تم».",
  "order.measure":
    "بس للطلبيات نوع We Pay، وبعد ما يكون العربون انسجّل فعلاً. بيظهر بعد المدة من تاريخ الطلبية طالما ناقص واحد من الثلاثة: T.CBM أو T.KG أو تاريخ التسليم. بتعبّيهم من الإشعار نفسه فبيختفي وبتصير الطلبية READY.",
  "order.delivery_note":
    "لأي طلبية (مش بس We Pay) مكتوب فيها نص بخانة «ملاحظات التسليم» ومربوطة بحاوية إلها يوم تحميل متوقع. بيظهر بعد المدة من يوم التحميل المتوقع - صفر يعني نفس اليوم - وبيختفي لما تضغط «تم» أو تأجّل يوم التحميل.",
  "order.missing":
    "أي طلبية Customer Pay ناقصها الحجم أو الوزن أو تاريخ التسليم - بتظهر من يوم إدخالها وبتروح لحالها لما تنعبى الثلاثة. طلبيات We Pay إلها إشعار «إدخال الحجم والوزن» الخاص فيها.",
  "order.snooze": "مش إشعار - هاي مدة رجوع أي إشعار طلبية ضغطت عليه «تخطي».",
  "order.upcoming":
    "مش إشعار - هاي شباك التسليمات القريبة: الطلبية بتدخل القائمة لما يضل على تاريخ تسليمها هالمدة أو أقل.",

  "board.auto_after":
    "الحاوية بتطلع على شاشة البورد لحالها بعد المدة من تاريخ تحميلها، إلا إذا حالتها delivered. والحاوية المثبّتة يدوياً بتظل ظاهرة مهما كان.",
};

/** The three families of notice, in the order they happen. */
const GROUPS = [
  { key: "container", title: "إشعارات الحاويات", icon: "inventory_2" },
  { key: "order", title: "إشعارات الطلبيات", icon: "receipt_long" },
  { key: "board", title: "شاشة البورد", icon: "cast" },
];

const currentUser = () => sessionStorage.getItem("user_name") || "";

/**
 * The inputs on this screen are read and typed into by whoever is tuning the
 * notices, so they get the same full-size treatment as the order form. At
 * MUI's "small" density the shrunken label sat on the border and the value
 * underneath it had no room - the number and the unit were there, but you
 * had to look for them.
 */
const FIELD_SX = {
  "& .MuiInputBase-root": { minHeight: 48, fontSize: 15, backgroundColor: "#fff" },
  "& .MuiInputBase-input": { padding: "12px 14px" },
  "& .MuiInputLabel-root": { fontSize: 14 },
};

/** One timing. Edited in place; nothing is saved until Save is pressed. */
function Row({ row, onSave, busy }) {
  const [amount, setAmount] = useState(String(row.amount));
  const [unit, setUnit] = useState(row.unit);
  const [enabled, setEnabled] = useState(row.enabled);

  // a save elsewhere reloads the list; take the server's word for it
  useEffect(() => {
    setAmount(String(row.amount));
    setUnit(row.unit);
    setEnabled(row.enabled);
  }, [row.amount, row.unit, row.enabled]);

  const dirty =
    String(row.amount) !== String(amount) || row.unit !== unit || row.enabled !== enabled;

  return (
    <SuiBox
      sx={({ palette, borders, functions: { rgba } }) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        px: 2.5,
        py: 2,
        borderRadius: borders.borderRadius.md,
        border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
        // a notice that is switched off should look switched off
        opacity: enabled ? 1 : 0.55,
        backgroundColor: dirty ? rgba(palette.primary.main, 0.06) : "transparent",
      })}
    >
      <SuiBox sx={{ flex: "1 1 420px", minWidth: 0 }}>
        <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <SuiTypography sx={{ fontSize: 16, fontWeight: 700 }}>{row.label}</SuiTypography>

          {row.changed && (
            <Chip
              label={`الأصل ${row.default}`}
              size="small"
              sx={({ palette, functions: { rgba } }) => ({
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: rgba(palette.warning.main, 0.18),
                color: palette.warning.focus,
              })}
            />
          )}
        </SuiBox>

        {/* the number alone means nothing without the date it counts from */}
        <SuiTypography sx={{ fontSize: 13, opacity: 0.7, mt: 0.25 }}>
          {unit === "minutes"
            ? `${amount} دقيقة من إنشاء السجل`
            : `${amount} ${Number(amount) === 1 ? "يوم" : "يوم"} من ${row.from}`}
        </SuiTypography>

        {/* and the whole rule, so nobody has to read the code to know
            whether a notice applies to a given container or order */}
        {WHEN[row.key] && (
          <SuiBox sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mt: 0.75 }}>
            <Icon sx={{ fontSize: "16px !important", opacity: 0.45, mt: 0.15 }}>info</Icon>
            <SuiTypography sx={{ fontSize: 12.5, lineHeight: 1.6, opacity: 0.78 }}>
              {WHEN[row.key]}
            </SuiTypography>
          </SuiBox>
        )}
      </SuiBox>

      <TextField
        type="number"
        label="المقدار"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        inputProps={{ min: 0, max: 3650, style: { textAlign: "center", fontWeight: 700 } }}
        InputLabelProps={{ shrink: true }}
        sx={{ ...FIELD_SX, width: 116 }}
      />

      <TextField
        select
        label="الوحدة"
        value={unit}
        onChange={(e) => setUnit(e.target.value)}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        sx={{ ...FIELD_SX, width: 140, "& select": { fontSize: 15, fontWeight: 600 } }}
      >
        <option value="days">أيام</option>
        <option value="minutes">دقائق</option>
      </TextField>

      <SuiBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <SuiTypography sx={{ fontSize: 14, fontWeight: 600, opacity: 0.75, minWidth: 52 }}>
          {enabled ? "فعّال" : "موقوف"}
        </SuiTypography>
      </SuiBox>

      {/* filled only when there is something to save; a grey filled block
          read as a broken button rather than as "nothing changed yet" */}
      <Button
        variant={dirty ? "contained" : "outlined"}
        color="primary"
        disabled={!dirty || busy}
        onClick={() => onSave(row.key, { amount: Number(amount) || 0, unit, enabled })}
        sx={{ minWidth: 96, height: 44, fontSize: 14 }}
      >
        حفظ
      </Button>
    </SuiBox>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    from: PropTypes.string,
    amount: PropTypes.number,
    unit: PropTypes.string,
    enabled: PropTypes.bool,
    default: PropTypes.number,
    changed: PropTypes.bool,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

Row.defaultProps = { busy: false };

function NotificationSettingsPage() {
  const [settings, setSettings] = useState([]);
  const [ready, setReady] = useState(true);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  const load = async () => {
    try {
      const res = await fetch(`${API}/notification-settings`, { cache: "no-store" });
      const json = await res.json();

      setReady(json.ready !== false);
      setNote(json.note || "");
      setSettings(Array.isArray(json.settings) ? json.settings : []);
      setError("");
    } catch (e) {
      setError("تعذّر تحميل الإعدادات");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (key, patch) => {
    setBusy(true);
    setSaved("");

    try {
      const res = await axios.put(
        `${API}/notification-settings`,
        { key, ...patch, user_name: currentUser() },
        { headers: { Accept: "application/json", "Content-Type": "application/json" } }
      );

      // the server sends the whole set back, so the screen never drifts
      // from what was actually stored
      if (Array.isArray(res?.data?.settings)) setSettings(res.data.settings);
      setSaved(key);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "تعذّر الحفظ");
    }

    setBusy(false);
  };

  const reset = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("إرجاع كل التوقيتات للأصل؟")) return;

    setBusy(true);
    try {
      const res = await axios.post(`${API}/notification-settings/reset`);
      if (Array.isArray(res?.data?.settings)) setSettings(res.data.settings);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "تعذّر الإرجاع");
    }
    setBusy(false);
  };

  const changed = settings.filter((s) => s.changed).length;
  const testing = settings.filter((s) => s.unit === "minutes").length;

  return (
    <DashboardLayout>
      {/**
       * The whole screen is Arabic, so it is laid out Arabic: the name of the
       * notice sits at the right where reading starts, and the number, the
       * unit, the switch and Save follow it leftwards. It was reading in the
       * order an English screen would, which put the label the eye looks for
       * first at the far end of the row.
       *
       * Only this page is turned - the rest of the dashboard is still mixed
       * English and Arabic and reads left to right.
       */}
      <SuiBox py={1} dir="rtl" sx={{ textAlign: "right" }}>
        <SuiBox mb={3}>
          <Card>
            <SuiBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                px: 3,
                py: 2.5,
              }}
            >
              <SuiBox sx={{ flex: "1 1 auto", minWidth: 0 }}>
                <SuiTypography variant="h6">إعدادات الإشعارات</SuiTypography>
                <SuiTypography sx={{ fontSize: 13, opacity: 0.7 }}>
                  متى يظهر كل إشعار، وهل يظهر أصلاً
                </SuiTypography>
              </SuiBox>

              {changed > 0 && (
                <Chip
                  label={`${changed} معدّلة عن الأصل`}
                  size="small"
                  sx={({ palette, functions: { rgba } }) => ({
                    fontWeight: 700,
                    backgroundColor: rgba(palette.warning.main, 0.18),
                    color: palette.warning.focus,
                  })}
                />
              )}

              <Button
                variant="outlined"
                color="dark"
                size="small"
                onClick={reset}
                disabled={busy || changed === 0}
                startIcon={<Icon>restart_alt</Icon>}
              >
                إرجاع الكل للأصل
              </Button>
            </SuiBox>

            {!ready && (
              <SuiBox px={3} pb={2}>
                <Alert severity="info">
                  {`${note} — القيم تحت هي ما يعمل به النظام الآن، لكن لا يمكن حفظ أي تعديل قبل ذلك.`}
                </Alert>
              </SuiBox>
            )}

            {/* a system left on minutes by accident is a system that will
                shout at everybody tomorrow morning */}
            {testing > 0 && (
              <SuiBox px={3} pb={2}>
                <Alert severity="warning">
                  {`${testing} إشعار مضبوط على الدقائق — وضع اختبار. أرجعه لأيام بعد ما تخلص.`}
                </Alert>
              </SuiBox>
            )}

            {error && (
              <SuiBox px={3} pb={2}>
                <Alert severity="error">{error}</Alert>
              </SuiBox>
            )}

            {saved && (
              <SuiBox px={3} pb={2}>
                <Alert severity="success">تم الحفظ</Alert>
              </SuiBox>
            )}

            {loading ? (
              <CargoLoader label="تحميل الإعدادات" />
            ) : (
              <SuiBox px={3} pb={3}>
                {GROUPS.map((g) => {
                  const rows = settings.filter((s) => s.group === g.key);
                  if (rows.length === 0) return null;

                  return (
                    <SuiBox key={g.key} mb={3}>
                      <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Icon sx={({ palette }) => ({ color: palette.primary.main })}>
                          {g.icon}
                        </Icon>
                        <SuiTypography sx={{ fontSize: 15, fontWeight: 800 }}>
                          {g.title}
                        </SuiTypography>
                      </SuiBox>

                      <Divider sx={{ mt: 0, mb: 1.5 }} />

                      <SuiBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {rows.map((row) => (
                          <Row key={row.key} row={row} onSave={save} busy={busy} />
                        ))}
                      </SuiBox>
                    </SuiBox>
                  );
                })}
              </SuiBox>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      <Footer />
    </DashboardLayout>
  );
}

export default NotificationSettingsPage;
